import React, {ReactElement, createRef, RefObject, ReactNode} from 'react';
import {FilterMatchMode} from 'primereact/api';
import {Column, ColumnFilterElementTemplateOptions} from 'primereact/column';
import {Calendar} from 'primereact/calendar';
import MComponent from '../../Toolkit/Components/Component';
import WithRouterProps from '../../Toolkit/WithRouterProps';
import MPage, {MPageProps} from '../../Toolkit/Components/Shell/Page';
import MTable, {MTableProps} from '../../Toolkit/Components/Controls/Table';
import MToolbar from '../../Toolkit/Components/Controls/Toolbar';
import MSpacer from '../../Toolkit/Components/Controls/Spacer';
import MButton from '../../Toolkit/Components/Controls/Button';
import {Request, RequestMultiReply} from '../../Api/ControllerTypes/RequestTypes';
import WithRouter from '../../Toolkit/WithRouter';
import {RequestClient} from '../../Api/RequestClients';
import {withMsal, WithMsalProps} from '@azure/msal-react';
import NSwagAdapter from '../../Toolkit/Api/NSwagAdapter';
import MNSwagClientConfiguration from '../../Toolkit/Api/NSwagClientConfiguration';
import SafeDigAccessTokenProvider from '../../Api/TrafficManagementAccessTokenProvider';
import MMultiDropDown from '../../Toolkit/Components/Controls/MultiDropDown';
import {MDropDownItem} from '../../Toolkit/Components/Controls/DropDownOption';
import {CreateLockRequest, LockClient} from '../../Api/LockClient';
import {LockReply} from '../../Api/ControllerTypes/LockTypes';
import {MSuccessIcon} from '../../Toolkit/Icons';
import MessageDialog from '../../Dialogs/MessageDialog';
import MDropDown from '../../Toolkit/Components/Controls/DropDown';
import {MNullable} from '../../Toolkit/Utils';
import EnumContext from '../../Context/enum-context';

type RequestRow = {
    data: Request;
    commercialSla: number;
}

type Props = WithMsalProps & WithRouterProps & {};

/** */
type State = {
    page: MPageProps;
    table: MTableProps<RequestRow>;
    selectedRow?: RequestRow;
    resourceFilter: string[];
}

class ListCommercialsPage extends MComponent<Props, State> {
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>

    private readonly m_requestClient: NSwagAdapter<RequestClient>;
    private readonly m_lockClient: NSwagAdapter<LockClient>;
    private readonly m_pageRef: RefObject<MPage>;
    private readonly m_messageDialogRef: RefObject<MessageDialog>;
    private m_interval: any = 0;

    /** */
    public constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.m_requestClient = new NSwagAdapter<RequestClient>(new RequestClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new SafeDigAccessTokenProvider(this.props.msalContext))));
        this.m_lockClient = new NSwagAdapter<LockClient>(new LockClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new SafeDigAccessTokenProvider(this.props.msalContext))));
        this.m_pageRef = createRef<MPage>();
        this.m_messageDialogRef = createRef<MessageDialog>();

        this.initializeState();

    }

    private bindMethods(): void {

        this.isOpenButtonDisabled = this.isOpenButtonDisabled.bind(this);
        this.requests_Click = this.requests_Click.bind(this);
        this.bookings_Click = this.bookings_Click.bind(this);
        this.crews_Click = this.crews_Click.bind(this);
        this.calendarView_Click = this.calendarView_Click.bind(this);
        this.dayView_Click = this.dayView_Click.bind(this);
        this.invoices_Click = this.invoices_Click.bind(this);
        this.drawings_Click = this.drawings_Click.bind(this);
        this.table_onSelectionChange = this.table_onSelectionChange.bind(this);
        this.page_onLoadData = this.page_onLoadData.bind(this);
        this.getToolbarTemplate = this.getToolbarTemplate.bind(this);
        this.getProjectFilterTemplate = this.getProjectFilterTemplate.bind(this);
        this.getWorkStreamFilterTemplate = this.getWorkStreamFilterTemplate.bind(this);
        this.commercialStatusFilterTemplate = this.commercialStatusFilterTemplate.bind(this);
        this.getResourceFilterTemplate = this.getResourceFilterTemplate.bind(this);
        this.getExtInvoicesFilterTemplate = this.getExtInvoicesFilterTemplate.bind(this);
        this.dfeStatusFilterTemplate = this.dfeStatusFilterTemplate.bind(this);
    }

    /** */
    private initializeState(): void {

        //  Page
        const page: MPageProps = {
            locked: false
        };

        //  Table
        const table: MTableProps<RequestRow> = {
            rowData: [],
            filters: {
                'data.estimate': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'data.requestor': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'data.projectValue': {
                    value: null,
                    matchMode: FilterMatchMode.IN
                },
                'data.exchange': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'data.workStream': {
                    value: null,
                    matchMode: FilterMatchMode.IN
                },
                'data.commercialStatus': {
                    value: null,
                    matchMode: FilterMatchMode.IN
                },
                'data.resources': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'data.externalInvoicesReceived': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'data.dfeStatus': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                }
            }
        };

        this.state = {
            page: page,
            table: table,
            selectedRow: undefined,
            resourceFilter: []
        };

    }

    public componentDidMount() : void {
        this.m_interval = setInterval(this.page_onLoadData, 300000);
    }

    public componentWillUnmount(): void {
        clearInterval(this.m_interval);
    }

    private isOpenButtonDisabled(): boolean {
        return this.state.selectedRow === undefined;
    }

    private requests_Click(): void {
        this.props.navigate(`/requests`);
    }

    private bookings_Click(): void {
        this.props.navigate(`/bookings`);
    }

    private calendarView_Click(): void {
        this.props.navigate('/calendarview');
    }

    private crews_Click(): void {
        this.props.navigate('/crews');
    }

    private dayView_Click(): void {
        this.props.navigate('/dayview');
    }

    private invoices_Click(): void {
        this.props.navigate('/invoices');
    }

    private drawings_Click(): void {
        this.props.navigate('/drawings');
    }

    private async table_onSelectionChange(selection: RequestRow | RequestRow[] | undefined): Promise<void> {

        this.safeSetState((draft: State) =>
            draft.selectedRow = selection as RequestRow | undefined);

        const navigateTo = selection as RequestRow | undefined
        if(navigateTo !== undefined) {
            const userName = this.props.msalContext.accounts[0].username;

            const lockResult = await this.m_lockClient.callWithReply(
                LockReply, (client) => client.get(navigateTo!.data.id)
            );
            lockResult.throwIfFailed();
            if(lockResult.reply!.lockData!.isLocked && lockResult.reply!.lockData!.lockingUser !== userName){
                this.m_messageDialogRef.current!.show(
                    'Booking in use',
                    `Booking ${navigateTo.data.estimate} is currently in use by '${lockResult.reply!.lockData!.lockingUser}'`,
                    MSuccessIcon);
            }
            else{
                await this.m_lockClient.call(
                    (client) => client.create(new CreateLockRequest(
                        {
                             itemId: navigateTo.data.id
                        })
                ));
                this.props.navigate(`/commercials/${navigateTo!.data.id}`);
            }
        }

    }

    private async page_onLoadData(): Promise<void> {
        //Get Row Data
        const listResult = await this.m_requestClient.callWithReply(
            RequestMultiReply, (client) => client.getCommercialList());
        listResult.throwIfFailed();

        //Create resource filter
        let filterList: string[] = [];

        listResult.reply!.requests.forEach((r: Request) => {
            //Create resource filter.
            filterList.push(...r.resources)
        });

        this.safeSetState((draft: State) => {

            draft.table.rowData = listResult.reply!.requests.map((r: Request) => {
                //Add SLA days
                const checkDate = r.commercialStatus === 2
                                  ? r.commercialCompleteDate ?? new Date()
                                  : new Date();
                const completionDate = r.quoteStatus === 5
                                       ? r.quoteCompleteDate
                                       : r.jobCompletedDate;
                const dateDiff = checkDate.getTime() - (completionDate?.getTime() ?? 0);
                const commercialSla = Math.floor(dateDiff / (1000 * 60 * 60 * 24));

                return {
                    data: r, commercialSla: commercialSla} as RequestRow;
            });
            draft.resourceFilter = filterList.filter((value, index) => filterList.indexOf(value) === index);

        });
    }

    private getToolbarTemplate(): ReactElement<MToolbar> {

        return (
            <MToolbar>
                <MButton label="CAD Requests"
                         color="info"
                         icon={"pi pi-question"}
                         iconOnly={false}
                         disabled={false}
                         onClick={this.requests_Click}/>
                <MSpacer/>
                <MButton label="Bookings"
                         color={"info"}
                         icon={"pi pi-book"}
                         iconOnly={false}
                         disabled={false}
                         onClick={this.bookings_Click}/>
                <MSpacer/>
                <MButton label="TM Crews"
                         color="info"
                         icon={"pi pi-users"}
                         iconOnly={false}
                         disabled={false}
                         onClick={this.crews_Click}/>
                <MSpacer/>
                <MButton label="Calendar View"
                         color="info"
                         icon={"pi pi-calendar"}
                         iconOnly={false}
                         disabled={false}
                         onClick={this.calendarView_Click}/>
                <MSpacer/>
                <MButton label="Day View"
                         color="info"
                         icon={"pi pi-calendar"}
                         iconOnly={false}
                         disabled={false}
                         onClick={this.dayView_Click}/>
                <MSpacer/>
                <MButton label="Purchase Orders"
                         color="info"
                         icon={"pi pi-pound"}
                         iconOnly={false}
                         disabled={false}
                         onClick={this.invoices_Click}/>
                <MSpacer/>
                <MButton label="Drawings"
                         color="info"
                         icon={"pi pi-images"}
                         disabled={false}
                         onClick={this.drawings_Click}/>
            </MToolbar>
        );

    }

    private dateFilterTemplate = (options: any) => {
        return <Calendar value={options.value}
                         onChange={(e: any) => options.filterApplyCallback(e.value, options.index)}
                         dateFormat="dd/mm/yy"
                         placeholder="dd/mm/yyyy"
                         mask="99/99/9999"/>;
    };

    private getProjectFilterTemplate(options: ColumnFilterElementTemplateOptions): ReactNode {
        if(this.context.enums === null) {
            return <div></div>;
        }

        const itemList : MDropDownItem<number>[] = (
            this.context.enums!.projectList
                .map((enumvalue) => {
                    return {label: enumvalue.code + ' - ' + enumvalue.name, value: enumvalue.id};
                })
        );

        return (
            <MMultiDropDown id="projectFilter"
                            items={itemList}
                            disabled={false}
                            onChange={(selection: number[]) => options.filterApplyCallback(selection)}/>
        );
    }

    private getWorkStreamFilterTemplate(options: ColumnFilterElementTemplateOptions): ReactNode {
        if(this.context.enums === null) {
            return <div></div>;
        }

        const itemList : MDropDownItem<number>[] = (
            this.context.enums!.workStreams
                .map((enumvalue) => {
                    return {label: enumvalue.name, value: enumvalue.id};
                })
        );

        return (
            <MMultiDropDown id="projectFilter"
                            items={itemList}
                            disabled={false}
                            onChange={(selection: number[]) => options.filterApplyCallback(selection)}/>
        );
    }

    private commercialStatusFilterTemplate(options: ColumnFilterElementTemplateOptions): ReactNode {
        if(this.context.enums === null) {
            return <div></div>;
        }

        const itemList : MDropDownItem<number>[] = (
            this.context.enums!.commercial
                .map((enumvalue) => {
                    return {label: enumvalue.name, value: enumvalue.id};
                })
        );

        return (
            <MMultiDropDown id="commercialFilter"
                            items={itemList}
                            disabled={false}
                            onChange={(selection: number[]) => options.filterApplyCallback(selection)}/>
        );
    }

    private getResourceFilterTemplate (options: ColumnFilterElementTemplateOptions): ReactNode {
        if(this.context.enums === null) {
            return <div></div>;
        }

        const itemList : MDropDownItem<string>[] = (
            this.state.resourceFilter
                .map((enumvalue) => {
                    return {label: enumvalue, value: enumvalue};
                })
        );

        return (
            <MDropDown id="resourceFilter"
                            items={itemList}
                            disabled={false}
                            onChange={(selection: MNullable<string>) => options.filterApplyCallback(selection)}/>
        );
    }

    private getExtInvoicesFilterTemplate (options: ColumnFilterElementTemplateOptions): ReactNode {
        if(this.context.enums === null) {
            return <div></div>;
        }

        const itemList : MDropDownItem<number>[] = (
            this.context.enums!.extInvoice
                .map((enumvalue) => {
                    return {label: enumvalue.name, value: enumvalue.id};
                })
        );

        return (
            <MMultiDropDown id="extInvoiceFilter"
                       items={itemList}
                       disabled={false}
                       onChange={(selection: number[]) => options.filterApplyCallback(selection)}/>
        );
    }

    private dfeStatusFilterTemplate(options: ColumnFilterElementTemplateOptions): ReactNode {
        if(this.context.enums === null) {
            return <div></div>;
        }

        const itemList : MDropDownItem<number>[] = (
            this.context.enums!.dfeStatuses
                .map((enumvalue) => {
                    return {label: enumvalue.name, value: enumvalue.id};
                })
        );

        return (
            <MMultiDropDown id="dfeStatusFilter"
                            items={itemList}
                            disabled={false}
                            onChange={(selection: number[]) => options.filterApplyCallback(selection)}/>
        );
    }

    private projectTemplate = (rowData: RequestRow) => {
        const index = this.context.enums?.projectList.findIndex(value => value.id === rowData.data.projectValue);

        if(index === undefined || index === -1) {
            return null;
        }

        return <span style={{fontSize: 'x-small'}}>
            <div>{this.context.enums?.projectList[index!].code}</div>
            <div>{this.context.enums?.projectList[index!].name}</div>
        </span>;
    };

    private workStreamTemplate = (rowData: RequestRow) => {
        const index = this.context.enums?.workStreams.findIndex(value => value.id === rowData.data.workStream);

        if(index === undefined || index === -1) {
            return null;
        }

        return this.context.enums?.workStreams[index!].name;
    };

    private commercialStatusTemplate = (rowData: RequestRow) => {
        const index = this.context.enums?.commercial.findIndex(value => value.id === rowData.data.commercialStatus);

        if(index === undefined || index === -1) {
            return null;
        }

        return this.context.enums?.commercial[index!].name;
    };

    private resourceTemplate(rowData: RequestRow) : ReactElement {
        var resources = rowData.data.resources;
        if(resources.length > 0) {
            return <span>
                {resources.map(r => <div>{r}</div>)}
            </span>;
        }

        return <span style={{fontSize: 'x-small'}}>
            </span>;
    }

    private extInvoicesTemplate = (rowData: RequestRow) => {
        const index = this.context.enums?.extInvoice.findIndex(value => value.id === rowData.data.externalInvoicesReceived);

        if(index === undefined || index === -1) {
            return null;
        }

        return this.context.enums?.extInvoice[index!].name;
    };

    private dfeStatusTemplate = (rowData: RequestRow) => {
        const index = this.context.enums?.dfeStatuses.findIndex(value => value.id === rowData.data.dfeStatus);

        if(index === undefined || index === -1) {
            return null;
        }

        return this.context.enums?.dfeStatuses[index!].name;
    };

    public render(): ReactElement {
        return (
            <MPage title="Commercials"
                   locked={this.state.page.locked}
                   toolbar={this.getToolbarTemplate()}
                   onLoadData={this.page_onLoadData}
                   ref={this.m_pageRef}>

                <MessageDialog ref={this.m_messageDialogRef}/>

                <MTable
                    rowKey="data.estimate"
                    rowData={this.state.table.rowData}
                    filters={this.state.table.filters}
                    onSelectionChange={this.table_onSelectionChange}
                    sortMode={'multiple'}
                    rowsPerPage={this.state.table.rowData.length}
                    multiSortMeta={[{field: 'data.estimate', order: 1}]}>

                    <Column field="data.estimate"
                            header="Estimate"
                            filter={true}
                            sortable={true}
                            style={{width: '8%'}}/>
                    <Column field="data.locked"
                            header="Locked"
                            filter={false}
                            sortable={false}
                            body={(rowData: RequestRow) => rowData.data.locked ? 'Yes' : ''}
                            style={{width: '8%'}}/>
                    <Column field="data.requestor"
                            header="Requestor"
                            filter={true}
                            sortable={true}
                            style={{width: '8%'}}/>
                    <Column field="data.projectValue"
                            header="Project Code"
                            filter={true}
                            filterElement={this.getProjectFilterTemplate}
                            showClearButton={false}
                            showFilterMenu={false}
                            sortable={true}
                            body={this.projectTemplate}
                            style={{width: '12%'}}/>
                    <Column field="data.exchange"
                            header="Exchange"
                            filter={true}
                            showClearButton={false}
                            showFilterMenu={false}
                            sortable={true}
                            style={{width: '12%'}}/>
                    <Column field="data.workStream"
                            header="Work Stream"
                            filter={true}
                            filterElement={this.getWorkStreamFilterTemplate}
                            showClearButton={false}
                            showFilterMenu={false}
                            sortable={true}
                            body={this.workStreamTemplate}
                            style={{width: '12%'}}/>
                    <Column field="data.commercialStatus"
                            header="Status"
                            filter={true}
                            filterElement={this.commercialStatusFilterTemplate}
                            showClearButton={false}
                            showFilterMenu={false}
                            sortable={true}
                            body={this.commercialStatusTemplate}
                            style={{width: '12%'}}/>
                    <Column field="data.resources"
                            header="Resource"
                            filter={true}
                            filterElement={this.getResourceFilterTemplate}
                            showClearButton={true}
                            showFilterMenu={false}
                            sortable={true}
                            body={this.resourceTemplate}
                            style={{width: '12%'}}/>
                    <Column field="data.externalInvoicesReceived"
                            header="Invoice Received"
                            filter={true}
                            filterElement={this.getExtInvoicesFilterTemplate}
                            showClearButton={true}
                            showFilterMenu={false}
                            sortable={true}
                            body={this.extInvoicesTemplate}
                            style={{width: '12%'}}/>
                    <Column field='commercialSla'
                            header="SLA"
                            filter={false}
                            sortable={true}
                            style={{width: '12%'}}/>
                    <Column field="data.dfeStatus"
                            header="DFE Status"
                            filter={true}
                            filterElement={this.dfeStatusFilterTemplate}
                            showClearButton={false}
                            showFilterMenu={false}
                            sortable={true}
                            body={this.dfeStatusTemplate}
                            style={{width: '12%'}}/>
                </MTable>
            </MPage>
        );
    }
}

export default withMsal(WithRouter(ListCommercialsPage));